import { type Ref, onBeforeUnmount, onMounted, ref, watch } from "vue";

import throttle from "lodash/throttle";

import { ensurePositionWithinViewport } from "@/helpers/elements";
import { useLocalStorageSetting } from "@/hooks/options";

import type {
  IDashboardSettings,
  ModalOptions,
  ResizableEventValues,
} from "@/models/common";

export const useResizable = (
  options: Ref<Partial<ModalOptions>>,
  draggable: Ref<HTMLElement | null>
) => {
  // Initialize center position immediately if no saved position exists
  if ((options.value.left ?? -1) < 0 || (options.value.top ?? -1) < 0) {
    const { width = 200, height = 200 } = options.value;
    const centerX = (window.innerWidth - width) / 2;
    const centerY = (window.innerHeight - height) / 2;

    options.value = {
      ...options.value,
      left: centerX,
      top: centerY,
      x: centerX,
      y: centerY,
    };
  }

  const repositionAsNeeded = async (force = true) => {
    const { left: currentLeft, top: currentTop } = options.value;

    const updates = await ensurePositionWithinViewport(
      options.value,
      draggable.value?.getBoundingClientRect()
    );

    if (!force && currentLeft === updates.x && currentTop === updates.y) {
      return;
    }

    options.value = {
      ...options.value,
      left: updates.x,
      top: updates.y,
    };
  };

  const updatePosition = async (positionAndEvent: ResizableEventValues) => {
    options.value = {
      ...options.value,
      x: positionAndEvent.left,
      y: positionAndEvent.top,
      width: positionAndEvent.width,
      height: positionAndEvent.height,
    };
    await repositionAsNeeded();
  };

  const resizeListener = throttle(
    async () => {
      await repositionAsNeeded(false);
    },
    100,
    { leading: false }
  );

  onMounted(async () => {
    // Ensure position is properly set and within viewport bounds
    await repositionAsNeeded();
    window.addEventListener("resize", resizeListener);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", resizeListener);
  });

  return {
    updatePosition,
    resizeListener,
    repositionAsNeeded,
  };
};

export const useResizableActivitySidebar = ({
  key,
  minWidth,
  defaultWidth,
  actions,
}: {
  key: keyof IDashboardSettings;
  minWidth: number;
  defaultWidth?: number;
  actions?: {
    onMaxWidthError?: () => void;
  };
}) => {
  const SIDEBAR_WIDTH_ALLOWANCE = 220;

  const sidebarWidth: Ref<number> = useLocalStorageSetting(
    key,
    defaultWidth ?? minWidth
  );

  const maxWidth = ref<number>(window.innerWidth - SIDEBAR_WIDTH_ALLOWANCE);
  const offsetLeft = ref<number | string>("70%");
  let animationFrameId: number;

  const calculateOffsetLeft = () => {
    const width = sidebarWidth.value;
    const viewPortWidth = window.innerWidth;
    const offset = viewPortWidth - width;
    return offset > SIDEBAR_WIDTH_ALLOWANCE ? offset : SIDEBAR_WIDTH_ALLOWANCE;
  };

  const calculateMaxWidth = () => {
    // window width minus left sidebar width
    const viewPortWidth = window.innerWidth;
    const maxWidth = viewPortWidth - SIDEBAR_WIDTH_ALLOWANCE;

    if (sidebarWidth.value > maxWidth) {
      sidebarWidth.value = maxWidth;
    }

    if (maxWidth < minWidth) {
      actions?.onMaxWidthError?.();
      return minWidth;
    }
    return maxWidth;
  };

  const updateWidth = (payload: ResizableEventValues) => {
    const { width } = payload;
    sidebarWidth.value = width;
  };

  const resizeListener = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
    animationFrameId = requestAnimationFrame(() => {
      repositionAsNeeded();
    });
  };

  const repositionAsNeeded = () => {
    maxWidth.value = calculateMaxWidth();
    offsetLeft.value = calculateOffsetLeft();
  };

  watch(sidebarWidth, () => {
    offsetLeft.value = calculateOffsetLeft();
  });

  onMounted(() => {
    offsetLeft.value = calculateOffsetLeft();
    maxWidth.value = calculateMaxWidth();
    window.addEventListener("resize", resizeListener);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", resizeListener);
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
  });

  return {
    offsetLeft,
    maxWidth,
    sidebarWidth,
    updateWidth,
  };
};
